import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ArrowLeft, ArrowRight } from "lucide-react";

import "swiper/css";
import "swiper/css/navigation";

const testimonials = [
    {
        text: `I was struggling to recover my shares from the IEPF for years, but thanks to Gantavyam, I was able to get them back. Their team was professional, efficient, and kept me updated throughout the process. I highly recommend their services to anyone facing similar issues.`,
        name: "Rahul Sharma",
        role: "Recovered shares of Reliance Industries Ltd.",
    },
    {
        text: `I had lost my physical shares of HDFC Bank Ltd. and thought they were gone forever. But Gantavyam's team helped me track them down and recover them. Their expertise and guidance made the process smooth and hassle-free. Thank you, Gantavyam!`,
        name: "Smita Patel",
        role: "Recovered shares of HDFC Bank Ltd.",
    },
    {
        text: `My father had passed away, leaving behind a portfolio of shares that were transferred to the IEPF. Gantavyam helped me recover those shares and transfer them to my name. Their team was sensitive to my situation and provided excellent support throughout the process.`,
        name: "Amit Kumar",
        role: "Recovered shares of Infosys Ltd.",
    },
    {
        text: `I had lost my share certificates of Tata Consultancy Services Ltd. and was worried about losing my investment. But Gantavyam's team helped me obtain duplicate certificates and recover my shares. Their prompt response and expertise saved me from a potential financial loss.`,
        name: "Rajesh Singh",
        role: "Recovered shares of Tata Consultancy Services Ltd.",
    },
    {
        text: `I was struggling to claim my dividends from the IEPF, but Gantavyam's team helped me navigate the process and recover my dues. Their knowledge of the IEPF rules and regulations was impressive, and their support was invaluable. I highly recommend their services.`,
        name: "Priya Jain",
        role: "Recovered dividends worth ₹50,000 from the IEPF.",
    },
    {
        text: `The team at Gantavyam helped me retrieve shares lost for over a decade. Their professionalism and constant updates made the whole process seamless.`,
        name: "Vikram Desai",
        role: "Recovered shares of Larsen & Toubro Ltd.",
    },
];

export default function Testimonials() {
    return (
        <section
            id="testimonials"
            className="py-20 relative overflow-hidden"
            style={{
                background: "linear-gradient(180deg, var(--bg-surface), var(--bg-base))",
            }}
        >
            <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
                {/* Decorative quote */}
                <img
                    src="/assets/images/Quote-mark.png"
                    alt=""
                    aria-hidden="true"
                    className="absolute left-4 -top-4 lg:left-0 lg:-top-8 w-20 h-20 opacity-60 select-none pointer-events-none"
                    draggable="false"
                />

                {/* Header */}
                <div className="flex items-start justify-between mb-6 lg:mb-10">
                    <div className="max-w-lg">
                        <h2
                            className="text-3xl lg:text-4xl font-extrabold leading-tight"
                            style={{ color: "var(--text-primary)" }}
                        >
                            Real Stories from Real Customers
                        </h2>
                        <p className="mt-4 text-base" style={{ color: "var(--text-secondary)" }}>
                            Get inspired by these stories.
                        </p>
                    </div>
                </div>

                {/* Swiper Slider */}
                <Swiper
                    modules={[Navigation]}
                    navigation={{
                        // we use class selector buttons placed beneath the Swiper
                        prevEl: ".swiper-prev",
                        nextEl: ".swiper-next",
                    }}
                    spaceBetween={24}
                    slidesPerView={3}
                    slidesPerGroup={3}
                    breakpoints={{
                        0: {
                            slidesPerView: 1,
                            slidesPerGroup: 1,
                            spaceBetween: 16,
                        },
                        640: {
                            slidesPerView: 2,
                            slidesPerGroup: 2,
                            spaceBetween: 20,
                        },
                        1024: {
                            slidesPerView: 3,
                            slidesPerGroup: 3,
                            spaceBetween: 24,
                        },
                    }}
                    className="pb-2"
                >
                    {testimonials.map((t, i) => (
                        <SwiperSlide key={i} className="h-auto">
                            <article className="rounded-xl border border-[var(--border)] bg-[var(--bg-surface)]
                                transition-all duration-300 flex flex-col w-full h-full 
                                overflow-visible transform-gpu 
                                hover:border-[var(--color-primary)]">
                                {/* make the article a flex column so internal elements can stretch */}
                                <div className="p-6 lg:p-8 flex flex-col justify-between w-full">
                                    {/* testimonial text: wrapped in quotes and italic */}
                                    <p
                                        className="
    leading-6 text-base text-justify italic text-[var(--text-secondary)]
    md:[display:-webkit-box] md:[-webkit-line-clamp:8] md:[-webkit-box-orient:vertical]
    md:h-[12rem] md:overflow-hidden
    "
                                    >
                                        &ldquo;{t.text}&rdquo;
                                    </p>

                                    {/* footer (author & role) */}
                                    <div className="mt-5">
                                        <p className="font-semibold" style={{ color: "var(--text-primary)" }}>
                                            {t.name}
                                        </p>
                                        <p className="text-sm" style={{ color: "var(--text-disabled)" }}>
                                            {t.role}
                                        </p>
                                    </div>
                                </div>
                            </article>
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* Navigation buttons (placed below the cards) */}
                <div className="mt-6 flex items-center justify-center gap-4">
                    {/* Desktop buttons (visible on lg and above) */}
                    <div className="hidden lg:flex items-center gap-4 mt-2">
                        <button
                            className="swiper-prev w-11 h-11 rounded-full flex items-center justify-center 
             bg-white border border-2 border-[var(--color-primary)] 
             text-[var(--color-primary)] shadow-sm transition 
             hover:bg-[var(--color-primary)] hover:text-white 
             active:scale-95"
                            aria-label="Previous testimonials"
                        >
                            <ArrowLeft size={18} strokeWidth={2} />
                        </button>

                        <button
                            className="swiper-next w-11 h-11 rounded-full flex items-center justify-center 
                         text-white shadow-sm transition hover:shadow-md hover:-translate-y-[2px] active:scale-95"
                            style={{ backgroundColor: "var(--color-primary)" }}
                            aria-label="Next testimonials"
                        >
                            <ArrowRight size={18} strokeWidth={2} />
                        </button>
                    </div>

                    {/* Mobile buttons (visible below cards on small screens) */}
                    <div className="flex lg:hidden items-center gap-4">
                        <button
                            className="swiper-prev w-10 h-10 rounded-full flex items-center justify-center bg-white border border-[var(--border)]"
                            style={{ color: "var(--text-primary)" }}
                            aria-label="Previous testimonials"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <button
                            className="swiper-next w-10 h-10 rounded-full flex items-center justify-center text-white"
                            style={{ backgroundColor: "var(--color-primary)" }}
                            aria-label="Next testimonials"
                        >
                            <ArrowRight size={18} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Small CSS adjustments specific to equal-height behavior on desktop.
          You can copy these into your global CSS if you prefer. */}
            <style>{`
        /* Make swiper slides stretch to equal height (desktop) */
        @media (min-width: 1024px) {
          /* Ensure wrapper aligns slides to stretch so each slide will match height of the tallest */
          .swiper-wrapper {
            align-items: stretch;
          }
          /* Make each slide a flex column to allow internal flexing */
          .swiper-slide {
            display: flex;
            height: auto !important;
            align-items: stretch;
          }
          .swiper-slide > article {
            /* ensure the article expands to fill the slide's height */
            display: flex;
            flex-direction: column;
            width: 100%;
          }
          /* Let the paragraph take remaining vertical space so footers sit aligned */
          .testimonial-text {
            flex: 1 1 auto;
          }
        }

        /* Optional: keep the quoted italic look across all sizes */
        .testimonial-text {
          font-style: italic;
        }
      `}</style>
        </section>
    );
}
